import { create } from "zustand"
import { immer } from "zustand/middleware/immer"
import { AoiName } from "../models/aois/AoiName"
import { AoiShape } from "../models/aois/AoiShape"
import { Project, ProjectData } from "../models/projects/Project"
import { ProjectDetails } from "../models/projects/ProjectDetails"

export enum AoiEditMode {
    SUBMIT = "SUBMIT",
    EDIT = "EDIT",
    SELECT = "SELECT",
}

interface ProjectEditState {
    project?: Project
    aoiList?: AoiName[]
    selectedAoi?: AoiShape
    projectEdited: boolean
    aoiEdited: boolean
    doPolygonRepairs: boolean
    deletingAoi: boolean
    parsingAoi: boolean
    aoiEditMode: AoiEditMode

    /**
     * Error from any requests, displayed in the Project Editor Stepper
     */
    error?: string
}

interface ProjectEditActions {
    setSelectedAoi: (aoi?: AoiShape) => void
    setProjectData: (projectData: ProjectData) => void
    updateProjectDetailsField: <FieldType extends keyof ProjectDetails>(
        field: FieldType,
        value: ProjectDetails[FieldType],
    ) => Promise<void>
    updateAoiField: <FieldType extends keyof AoiShape>(field: FieldType, value: AoiShape[FieldType]) => Promise<void>
    submitNewAoi: () => void
    togglePolygonRepairs: () => void
    setDeletingAoi: (deleting: boolean) => void
    setParsingAoi: (parsing: boolean) => void
    setAoiEditMode: (mode: AoiEditMode) => void
    setError: (error?: string) => void
    reset: () => void
}

const initialState: Readonly<ProjectEditState> = {
    projectEdited: false,
    aoiEdited: false,
    doPolygonRepairs: false,
    deletingAoi: false,
    parsingAoi: false,
    aoiEditMode: AoiEditMode.SELECT,
    project: undefined,
    aoiList: undefined,
    selectedAoi: undefined,
    error: undefined,
}

/**
 * This zustand store helps share data between the component creating the state, FlightplanAcquisitionModeSelect.tsx
 * And the API call that pushes the data to the server.
 */
export const useProjectEditStore = create<ProjectEditState & ProjectEditActions>()(
    immer((set, get) => ({
        ...initialState,
        setSelectedAoi: (aoi?: AoiShape) => set({ selectedAoi: aoi }),
        setProjectData: (projectData: ProjectData) =>
            set({
                project: projectData.project,
                aoiList: projectData.aoi_list,
            }),
        updateProjectDetailsField: async (field, value) =>
            set((state) => {
                if (state.project == null) {
                    throw new Error("Project must be present to update project field.")
                }
                if (state.project.project_detail != null) {
                    state.project.project_detail[field] = value
                    state.projectEdited = true
                }
            }),
        updateAoiField: async (field, value) =>
            set((state) => {
                if (state.selectedAoi == null) {
                    throw new Error("Aoi must be present to update aoi field.")
                }
                state.selectedAoi[field] = value
                state.aoiEdited = true
            }),
        submitNewAoi: () => {
            const { project, aoiList } = get()
            if (project == null || aoiList == null) {
                throw new Error("Project and aoi list must be present to submit new AOI.")
            }

            const aoiCharCodes = [64]

            if (aoiList.length > 0) {
                aoiList.forEach((aoi) => {
                    const block = aoi.name.split("Block ")[1]

                    if (aoi.name.startsWith("Block ") && block.length === 1) {
                        const blockChar = block.charCodeAt(0)
                        if (blockChar > 64 && blockChar < 92) {
                            aoiCharCodes.push(blockChar)
                        }
                    }

                    aoiCharCodes.push(64) // "@" (before "A")
                })
            }

            const highestChar = Math.max(...aoiCharCodes)
            const newBlock = String.fromCharCode(highestChar + 1)

            set({
                doPolygonRepairs: false,
                aoiEdited: true,
                selectedAoi: {
                    name: newBlock !== "[" ? `Block ${newBlock}` : "",
                    project_number: project.project_number,
                    aoi_mode: [],
                    mode_ids: [],
                },
            })
        },
        togglePolygonRepairs: () => set({ doPolygonRepairs: !get().doPolygonRepairs }),
        setDeletingAoi: (deletingAoi: boolean) => set({ deletingAoi }),
        setParsingAoi: (parsingAoi: boolean) => set({ parsingAoi }),
        setAoiEditMode: (mode: AoiEditMode) => set({ aoiEditMode: mode }),
        setError: (error?: string) => set({ error }),
        reset: () => set(initialState),
    })),
)
